import axios from "axios";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail, Instagram, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { CONTACT, SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { icon: MessageCircle, label: "WhatsApp", value: CONTACT.phoneDisplay, href: CONTACT.whatsapp, testid: "contact-whatsapp-link" },
  { icon: Phone, label: "Call Us", value: CONTACT.phoneDisplay, href: CONTACT.phoneHref, testid: "contact-phone-link" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-email-link" },
  { icon: Instagram, label: "Instagram", value: "@saimohanconstructions", href: CONTACT.instagram, testid: "contact-instagram-link" },
];

const inputClass =
  "w-full bg-white border border-slate-300 px-4 py-3.5 text-sm text-prussian placeholder:text-slate-400 focus:outline-none focus:border-blueprint-accent focus:ring-1 focus:ring-blueprint-accent transition-[border-color,box-shadow] duration-300";

const Contact = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      const payload = { ...data, email: data.email || undefined, message: data.message || undefined };
      await axios.post(`${API}/enquiries`, payload);
      toast.success("Enquiry received. Our team will call you back within 24 hours.");
      reset();
    } catch {
      toast.error("Could not send your enquiry. Please call or WhatsApp us directly.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-slate-50 blueprint-grid-fine">
      <span className="absolute top-3 left-4 font-mono text-blueprint/50 select-none" aria-hidden>+</span>
      <span className="absolute bottom-3 right-4 font-mono text-blueprint/50 select-none" aria-hidden>+</span>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-blueprint-accent mb-6">
            Free Site Visit & Estimate
          </p>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-6xl text-prussian">
            Let's build<br />together.
          </h2>
          <p className="mt-6 text-sm md:text-base text-slate-600 leading-relaxed max-w-md">
            Share your plot details or project idea — we respond within one working
            day with an honest, itemised estimate.
          </p>

          <div className="mt-12 border-t border-slate-200">
            {CHANNELS.map((c) => (
              <a
                key={c.label}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={c.testid}
                className="group flex items-center justify-between gap-4 border-b border-slate-200 py-5 hover:bg-white transition-colors duration-300 px-2"
              >
                <span className="flex items-center gap-4">
                  <c.icon size={18} strokeWidth={1.75} className="text-blueprint-accent" />
                  <span>
                    <span className="block font-mono text-[9px] tracking-[0.3em] uppercase text-slate-400">
                      {c.label}
                    </span>
                    <span className="block text-sm md:text-base text-prussian font-medium mt-0.5">
                      {c.value}
                    </span>
                  </span>
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-slate-300 group-hover:text-blueprint-accent group-hover:translate-x-1 group-hover:-translate-y-1 transition-[transform,color] duration-300"
                />
              </a>
            ))}
          </div>

          <p data-testid="contact-address" className="mt-8 flex items-start gap-3 text-xs md:text-sm text-slate-500">
            <MapPin size={16} className="text-blueprint-accent shrink-0 mt-0.5" />
            {CONTACT.address}
          </p>
          <p data-testid="contact-proprietor" className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">
            J. Ramanjaneyulu — Proprietor
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="contact-enquiry-form"
            className="bg-white border border-blueprint-line p-7 md:p-10 relative"
          >
            <span className="absolute -top-px -left-px font-mono text-blueprint/60 bg-white px-1 select-none" aria-hidden>+</span>
            <span className="absolute -top-px -right-px font-mono text-blueprint/60 bg-white px-1 select-none" aria-hidden>+</span>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400 mb-8">
              Form / ENQ-01 — Project Enquiry
            </p>

            <div className="space-y-6">
              <div>
                <label htmlFor="enq-name" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                  Your Name *
                </label>
                <input
                  id="enq-name"
                  data-testid="contact-form-name-input"
                  placeholder="e.g. Ramesh Kumar"
                  className={inputClass}
                  {...register("name", { required: true, minLength: 2 })}
                />
                {errors.name && <p data-testid="contact-form-name-error" className="mt-1.5 text-xs text-red-600">Please enter your name.</p>}
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="enq-phone" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                    Phone *
                  </label>
                  <input
                    id="enq-phone"
                    data-testid="contact-form-phone-input"
                    placeholder="+91 ..."
                    className={inputClass}
                    {...register("phone", { required: true, minLength: 8 })}
                  />
                  {errors.phone && <p data-testid="contact-form-phone-error" className="mt-1.5 text-xs text-red-600">Valid phone required.</p>}
                </div>
                <div>
                  <label htmlFor="enq-email" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                    Email
                  </label>
                  <input
                    id="enq-email"
                    type="email"
                    data-testid="contact-form-email-input"
                    placeholder="you@example.com"
                    className={inputClass}
                    {...register("email")}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="enq-service" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                  Service Required *
                </label>
                <select
                  id="enq-service"
                  data-testid="contact-form-service-select"
                  className={inputClass}
                  {...register("service", { required: true })}
                >
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.title} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
                {errors.service && <p data-testid="contact-form-service-error" className="mt-1.5 text-xs text-red-600">Please choose a service.</p>}
              </div>

              <div>
                <label htmlFor="enq-message" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                  Project Details
                </label>
                <textarea
                  id="enq-message"
                  rows={4}
                  data-testid="contact-form-message-textarea"
                  placeholder="Plot size, location, budget range, timeline..."
                  className={`${inputClass} resize-none`}
                  {...register("message")}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-form-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-blueprint-accent text-white font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-prussian disabled:opacity-60 transition-colors duration-300"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Request Free Estimate <ArrowUpRight size={15} />
                  </>
                )}
              </button>
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400 text-center">
                Response within 24 working hours
              </p>
            </div>
          </form>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="border border-blueprint-line bg-white"
        >
          <div className="flex items-center justify-between border-b border-blueprint-line px-5 py-3">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">
              Fig. 03 — Find Us / Gachibowli X Roads
            </p>
            <a
              href="https://maps.google.com/?q=Sri+Sai+Padmasree,+Greenlands+Colony,+Gachibowli,+Hyderabad+500032"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-directions-link"
              className="font-mono text-[10px] tracking-[0.2em] uppercase text-blueprint-accent hover:text-prussian transition-colors duration-300"
            >
              Get Directions ↗
            </a>
          </div>
          <iframe
            title="Sai Mohan Constructions office location map"
            data-testid="contact-map-iframe"
            src="https://maps.google.com/maps?q=Sri%20Sai%20Padmasree%2C%20Greenlands%20Colony%2C%20Gachibowli%2C%20Hyderabad%20500032&t=&z=16&ie=UTF8&iwloc=&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-[320px] grayscale-[25%]"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
